/**
 * useWebSocket Composable
 *
 * Handles WebSocket connection to the AI Assistant API for real-time
 * transcription status updates.
 *
 * Uses V1-compatible event patterns:
 * - join-session-room (client -> server) with ai-assistant-{sessionId} format
 * - session-room-joined (server -> client)
 * - Events received via onAny() filtering by channel name
 */

import { ref, onMounted, onUnmounted } from "vue";
import { io, Socket } from "socket.io-client";
import { getWebSocketUrl, isLocalEnv } from "@/utils/IdentifyGlobalEnv";
import { UserTracking } from "@/utils/UserTracking";
import { trackNoaRumAction, NOA_RUM_EVENT } from "@/utils/noaObservability";

// =============================================================================
// Types
// =============================================================================

export type FormGenerationStatus = "started" | "processing" | "completed" | "failed";

export type FormGenerationEventData = {
  type: "form_generation";
  event: "form_generate_status_changed";
  status: FormGenerationStatus;
  generationId: string;
  formId: number;
  sessionId: string;
  data?: {
    needsFetch?: boolean;
    error?: string;
  };
};

export type TranscriptionStatus =
  | "recording"
  | "recorded"
  | "transcribing"
  | "transcribed"
  | "analyzing"
  | "summarized"
  | "error"
  | "interrupted";

export interface TranscriptionStatusPayload {
  status: TranscriptionStatus;
  transcription?: string[];
  summary?: string;
  parsedSummary?: string;
  reviewedSummary?: string;
  error?: string;
  doctorId?: string;
  facilityId?: string;
  episodeId?: string;
}

export type StatusChangeCallback = (status: string, data: TranscriptionStatusPayload) => void;

export interface WebSocketOptions {
  onDisconnect?: (reason: string) => void;
  onReconnect?: () => void;
  onRoomJoined?: () => void;
  /** Returns the clinic JWT sent in the Socket.IO handshake (required by the API). */
  getToken?: () => string | null | undefined;
}

// =============================================================================
// Main Composable
// =============================================================================

// Connection timeout for polling fallback (10 seconds)
const CONNECTION_TIMEOUT_MS = 10000;

export function useWebSocket(
  sessionId: string | null | undefined,
  onStatusChange: StatusChangeCallback,
  autoConnect = false,
  options?: WebSocketOptions,
) {
  const socketClient = ref<Socket | null>(null);
  const currentSessionId = ref<string | null>(sessionId || null);
  const isConnected = ref(false);
  const isReconnecting = ref(false);
  const reconnectionCount = ref(0);
  const lastDisconnectTime = ref<number | null>(null);
  const connectionError = ref<string | null>(null);

  // Connection timeout tracking for polling fallback
  const connectionAttemptFailed = ref(false);
  let connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Store callback references
  const statusChangeCallback = ref<StatusChangeCallback>(onStatusChange);
  const formGenerationCallbacks = ref<Map<string, (eventData: FormGenerationEventData) => void>>(new Map());

  /**
   * Update the status change callback
   */
  const updateStatusChangeCallback = (newCallback: StatusChangeCallback) => {
    statusChangeCallback.value = newCallback;
  };

  /**
   * Connect to WebSocket server and join session room
   * Uses V1-compatible patterns for room joining and event handling
   */
  const connect = (newSessionId?: string) => {
    const sessionIdToUse = newSessionId || currentSessionId.value;

    if (!sessionIdToUse) {
      console.warn("[useWebSocket] Cannot connect: sessionId is not available");
      return;
    }

    // Check if we're already connected to this session
    if (socketClient.value && currentSessionId.value === sessionIdToUse && isConnected.value) {
      return;
    }

    // Disconnect existing connection if switching to a different session
    if (socketClient.value) {
      if (currentSessionId.value) {
        const oldChannel = `ai-assistant-${currentSessionId.value}`;
        socketClient.value.emit("leave-session-room", { sessionId: oldChannel });
      }
      socketClient.value.disconnect();
      socketClient.value = null;
    }

    currentSessionId.value = sessionIdToUse;
    const serverUrl = getWebSocketUrl();

    // V1 pattern: channel name includes "ai-assistant-" prefix
    const socketChannel = `ai-assistant-${sessionIdToUse}`;

    // Reset connection attempt failed state and start timeout
    connectionAttemptFailed.value = false;
    if (connectionTimeoutId) {
      clearTimeout(connectionTimeoutId);
    }
    connectionTimeoutId = setTimeout(() => {
      if (!isConnected.value) {
        connectionAttemptFailed.value = true;
        console.warn("[useWebSocket] Connection timeout - will use polling fallback");
      }
    }, CONNECTION_TIMEOUT_MS);

    const socket = io(serverUrl, {
      // Connection options for better reliability
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // The API rejects the handshake without the clinic JWT (SEC-221).
      // A function is evaluated on every (re)connection attempt, so the token stays fresh.
      auth: (cb) => cb({ token: options?.getToken?.() || "" }),
    });

    // ==========================================================================
    // Connection Events
    // ==========================================================================

    socket.on("connect", () => {
      // Update connection state
      isConnected.value = true;
      connectionError.value = null;

      // Clear connection timeout since we connected successfully
      if (connectionTimeoutId) {
        clearTimeout(connectionTimeoutId);
        connectionTimeoutId = null;
      }
      connectionAttemptFailed.value = false;

      // Track reconnection
      const wasReconnecting = lastDisconnectTime.value !== null;
      if (wasReconnecting) {
        reconnectionCount.value++;
        isReconnecting.value = false;
        lastDisconnectTime.value = null;

        // Call reconnect callback if provided
        options?.onReconnect?.();
      }

      trackNoaRumAction(NOA_RUM_EVENT.WS_CONNECTED, {
        session_id: currentSessionId.value ?? "no-active-session",
        was_reconnecting: wasReconnecting,
        reconnection_count: reconnectionCount.value,
      });

      // V1 pattern: Use join-session-room event with full channel name
      socket.emit("join-session-room", {
        sessionId: socketChannel,
      });
    });

    // V1 pattern: Listen for session-room-joined confirmation
    socket.on("session-room-joined", (response: { status: string; roomName?: string; error?: string }) => {
      if (response.status !== "success") {
        console.error("[useWebSocket] Error joining room:", response.error);
        // Without the room no events arrive: let the polling fallback take over now.
        connectionAttemptFailed.value = true;
        connectionError.value = response.error ?? "join-failed";
        trackNoaRumAction(NOA_RUM_EVENT.WS_CONNECT_ERROR, {
          session_id: currentSessionId.value ?? "no-active-session",
          error_message: response.error ?? "room-join-failed",
          reconnection_count: reconnectionCount.value,
        });
        return;
      }
      trackNoaRumAction(NOA_RUM_EVENT.WS_ROOM_JOINED, {
        session_id: currentSessionId.value ?? "no-active-session",
        room_name: response.roomName,
      });
      options?.onRoomJoined?.();
    });

    socket.on("connect_error", (error) => {
      console.error("[useWebSocket] Connection error:", error.message);
      isConnected.value = false;
      connectionError.value = error.message;
      // Handshake rejected by auth middleware: socket.io does not retry, so fall back immediately.
      if (!socket.active) {
        connectionAttemptFailed.value = true;
      }

      UserTracking.trackEvent("noa_socket_connect_error", {
        session_id: currentSessionId.value ?? "no-active-session",
        error_message: error.message ?? "unknown",
        reconnection_count: String(reconnectionCount.value),
        page_url: window.location.href,
      });

      trackNoaRumAction(NOA_RUM_EVENT.WS_CONNECT_ERROR, {
        session_id: currentSessionId.value ?? "no-active-session",
        error_message: error.message ?? "unknown",
        reconnection_count: reconnectionCount.value,
      });
    });

    socket.on("disconnect", (reason) => {
      isConnected.value = false;
      lastDisconnectTime.value = Date.now();
      isReconnecting.value = true;

      UserTracking.trackEvent("noa_socket_disconnected", {
        session_id: currentSessionId.value ?? "no-active-session",
        reason,
        reconnection_count: String(reconnectionCount.value),
        page_url: window.location.href,
      });

      trackNoaRumAction(NOA_RUM_EVENT.WS_DISCONNECTED, {
        session_id: currentSessionId.value ?? "no-active-session",
        reason,
        reconnection_count: reconnectionCount.value,
      });

      options?.onDisconnect?.(reason);

      if (connectionTimeoutId) {
        clearTimeout(connectionTimeoutId);
      }
      connectionTimeoutId = setTimeout(() => {
        if (!isConnected.value) {
          connectionAttemptFailed.value = true;
        }
      }, CONNECTION_TIMEOUT_MS);
    });

    // ==========================================================================
    // V1 Pattern: Use onAny to capture events by channel name
    // ==========================================================================

    socket.onAny((eventName: string, ...args: unknown[]) => {
      // V1 pattern: Check if event name matches our channel
      if (eventName === socketChannel) {
        const msg = args[0] as {
          type?: string;
          event?: string;
          status?: string;
          data?: TranscriptionStatusPayload & { needsFetch?: boolean; error?: string };
          generationId?: string;
          formId?: number;
          sessionId?: string;
          doctorId?: string;
          facilityId?: string;
          episodeId?: string;
        };

        const { type, event, status, data } = msg;

        // Handle transcription status changes (V1 webhook pattern)
        if (type === "webhook" && event === "transcription_status_changed") {
          if (status && statusChangeCallback.value) {
            try {
              // Build payload compatible with callback expectations
              const payload: TranscriptionStatusPayload = {
                status: status as TranscriptionStatus,
                transcription: data?.transcription,
                summary: data?.summary,
                parsedSummary: data?.parsedSummary,
                reviewedSummary: data?.reviewedSummary,
                error: data?.error,
                doctorId: msg.doctorId,
                facilityId: msg.facilityId,
                episodeId: msg.episodeId,
              };

              statusChangeCallback.value(status, payload);
            } catch (error) {
              console.error("[useWebSocket] Error in callback:", error);
            }
          }
        }

        // Handle form generation status changes
        if (type === "form_generation" && event === "form_generate_status_changed") {
          // Call registered callback for this generationId
          if (msg.generationId) {
            const callback = formGenerationCallbacks.value.get(msg.generationId);
            if (callback) {
              callback(msg as unknown as FormGenerationEventData);

              // Remove callback if completed or failed (terminal states)
              if (status === "completed" || status === "failed") {
                formGenerationCallbacks.value.delete(msg.generationId);
              }
            }
          }
        }
      }
    });

    socketClient.value = socket;
  };

  /**
   * Register a callback for form generation status updates
   */
  const onFormGenerationStatus = (generationId: string, callback: (eventData: FormGenerationEventData) => void) => {
    formGenerationCallbacks.value.set(generationId, callback);
  };

  /**
   * Unregister a form generation callback
   */
  const offFormGenerationStatus = (generationId: string) => {
    formGenerationCallbacks.value.delete(generationId);
  };

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = () => {
    // Clear connection timeout
    if (connectionTimeoutId) {
      clearTimeout(connectionTimeoutId);
      connectionTimeoutId = null;
    }

    if (socketClient.value) {
      // V1 pattern: Leave session room before disconnecting
      if (currentSessionId.value) {
        const socketChannel = `ai-assistant-${currentSessionId.value}`;
        socketClient.value.emit("leave-session-room", {
          sessionId: socketChannel,
        });
      }

      socketClient.value.disconnect();
      socketClient.value = null;
      isConnected.value = false;
      formGenerationCallbacks.value.clear();
    }
  };

  // Auto-connect on mount if enabled
  onMounted(() => {
    if (autoConnect && currentSessionId.value) {
      connect();
    }

    // Expose socket state to window for dev panel StateTab
    if (typeof window !== "undefined" && isLocalEnv()) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as unknown as Record<string, unknown>).__aiAssistantSocketState = {
        get connected() {
          return isConnected.value;
        },
        get reconnecting() {
          return isReconnecting.value;
        },
        get reconnectionCount() {
          return reconnectionCount.value;
        },
        get connectionAttemptFailed() {
          return connectionAttemptFailed.value;
        },
      };
    }
  });

  // Auto-cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    socketClient,
    isConnected,
    isReconnecting,
    reconnectionCount,
    connectionError,
    connectionAttemptFailed,
    connect,
    disconnect,
    updateStatusChangeCallback,
    onFormGenerationStatus,
    offFormGenerationStatus,
  };
}
